import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  Building2, ArrowRight, ArrowLeft, CheckCircle, Save,
  Factory, Users, Gauge, GitBranch, BarChart3, Crown
} from "lucide-react";

import { API } from "../lib/api";

const INDUSTRIES = ["IT", "Construction", "Healthcare", "Finance", "Manufacturing", "Education", "Retail", "Government", "Other"];
const COMPANY_SIZES = ["Startup (1-10)", "Small (11-50)", "Medium (51-200)", "Large (201-1000)", "Enterprise (1000+)"];
const COMPLEXITIES = ["Low", "Medium", "High", "Very High"];
const METHODOLOGIES = ["Agile", "Waterfall", "Hybrid", "PMI", "Lean", "Other"];

const PLAN_LABELS = { free: "Free", individual: "Individual", team: "Team", enterprise: "Enterprise" };

export default function AdminSetupPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchingOrg, setFetchingOrg] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [existingOrg, setExistingOrg] = useState(null);
  const [form, setForm] = useState({
    name: "",
    industry: "IT",
    company_size: "Medium (51-200)",
    project_complexity: "Medium",
    methodology: "Agile",
  });

  // Fetch existing org on load
  useEffect(() => {
    axios.get(`${API}/api/org`, { withCredentials: true })
      .then(({ data }) => {
        if (data && data.id) {
          setExistingOrg(data);
          setForm({
            name: data.name || "",
            industry: data.industry || "IT",
            company_size: data.company_size || "Medium (51-200)",
            project_complexity: data.project_complexity || "Medium",
            methodology: data.methodology || "Agile",
          });
        }
      })
      .catch(() => {})
      .finally(() => setFetchingOrg(false));
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { setError("Organization name is required"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/api/org`, form, { withCredentials: true });
      if (refreshUser) await refreshUser();
      navigate("/admin/employees");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await axios.put(`${API}/api/org`, form, { withCredentials: true });
      if (refreshUser) await refreshUser();
      setSuccess("Organization settings updated successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingOrg) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Existing Org → Settings View ──
  if (existingOrg) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-8 animate-slide-up">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-5 h-5 text-slate-700" />
              <h1 className="font-outfit text-3xl font-black text-slate-900">Organization Settings</h1>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500">
              Manage your organization profile and context configuration
            </p>
          </div>

          {/* Plan Info */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 flex items-center justify-between animate-slide-up stagger-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-outfit font-bold text-slate-900 text-sm">
                  {PLAN_LABELS[user?.plan] || "Free"} Plan
                </p>
                <p className="font-ibm-plex text-xs text-slate-400">
                  {user?.assessments_used || 0} assessment{(user?.assessments_used || 0) !== 1 ? "s" : ""} used
                  {user?.plan_limits?.assessments > 0 && ` / ${user.plan_limits.assessments} limit`}
                </p>
              </div>
            </div>
            <button onClick={() => navigate("/pricing")} data-testid="view-pricing-btn"
              className="text-blue-600 font-outfit font-medium text-sm hover:text-blue-800 transition-colors">
              View Plans
            </button>
          </div>

          {/* Org Details Form */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5 animate-slide-up stagger-2">
            <div>
              <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                Organization Name
              </label>
              <input data-testid="org-name-input" type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  <Factory className="w-3 h-3 inline mr-1" /> Industry
                </label>
                <select data-testid="industry-select" value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  <Users className="w-3 h-3 inline mr-1" /> Company Size
                </label>
                <select data-testid="company-size-select" value={form.company_size}
                  onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  <Gauge className="w-3 h-3 inline mr-1" /> Project Complexity
                </label>
                <select data-testid="complexity-select" value={form.project_complexity}
                  onChange={(e) => setForm({ ...form, project_complexity: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  {COMPLEXITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  <GitBranch className="w-3 h-3 inline mr-1" /> Methodology
                </label>
                <select data-testid="methodology-select" value={form.methodology}
                  onChange={(e) => setForm({ ...form, methodology: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                  {METHODOLOGIES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">{error}</div>
            )}
            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 font-ibm-plex flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> {success}
              </div>
            )}

            <button data-testid="save-org-btn" onClick={handleUpdate} disabled={loading}
              className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-60">
              <Save className="w-4 h-4" /> {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-4 mt-6 animate-slide-up stagger-3">
            <button onClick={() => navigate("/admin/employees")} data-testid="go-employees-btn"
              className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-slate-400 hover:shadow-sm transition-all">
              <Users className="w-5 h-5 text-blue-600 mb-2" />
              <p className="font-outfit font-bold text-slate-900 text-sm">Employee Management</p>
              <p className="font-ibm-plex text-xs text-slate-400 mt-0.5">Add, import, and manage team members</p>
            </button>
            <button onClick={() => navigate("/pricing")} data-testid="go-pricing-btn"
              className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-slate-400 hover:shadow-sm transition-all">
              <Crown className="w-5 h-5 text-purple-600 mb-2" />
              <p className="font-outfit font-bold text-slate-900 text-sm">Upgrade Plan</p>
              <p className="font-ibm-plex text-xs text-slate-400 mt-0.5">Unlock team dashboards and alerts</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── New Org Setup Wizard ──
  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <img src="/synq-mark.png" alt="SYNQ" className="w-10 h-10 object-contain" draggable={false} />
          <span className="font-outfit text-white text-xl font-bold tracking-tight">SYNQ</span>
        </div>
        <div>
          <h1 className="font-outfit text-4xl font-black text-white leading-tight tracking-tight mb-4">
            Set Up Your<br />Organization
          </h1>
          <p className="font-ibm-plex text-slate-400 text-base leading-relaxed mb-10">
            Configure your intelligence platform. SYNQ adapts its assessment depth, scoring sensitivity, and insights based on your organization profile.
          </p>
          <div className="space-y-4">
            {[
              { num: 1, label: "Organization Details", desc: "Name your workspace" },
              { num: 2, label: "Context Configuration", desc: "Industry, size & methodology" },
            ].map((s) => (
              <div key={s.num} className={`flex items-start gap-4 p-4 rounded-lg border transition-all ${
                step === s.num ? "border-white/30 bg-white/5" : step > s.num ? "border-slate-700" : "border-slate-800"
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-outfit font-bold flex-shrink-0 ${
                  step > s.num ? "bg-emerald-500 text-white" : step === s.num ? "bg-white text-slate-900" : "bg-slate-700 text-slate-400"
                }`}>
                  {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
                </div>
                <div>
                  <p className={`font-outfit font-semibold text-sm ${step >= s.num ? "text-white" : "text-slate-500"}`}>{s.label}</p>
                  <p className="font-ibm-plex text-xs text-slate-500 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="font-ibm-plex text-xs text-slate-600">
          &copy; {new Date().getFullYear()} Giridhar Alwar. All rights reserved.
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-outfit text-slate-900 text-lg font-bold">SYNQ — Setup</span>
          </div>

          {step === 1 && (
            <div className="animate-slide-up">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-slate-700" />
                <h2 className="font-outfit text-2xl font-black text-slate-900">Organization Details</h2>
              </div>
              <p className="font-ibm-plex text-sm text-slate-500 mb-8">
                This is your workspace name visible to your team.
              </p>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Organization Name *
                </label>
                <input data-testid="org-name-input" type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Technologies"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" />
              </div>
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">{error}</div>
              )}
              <button data-testid="setup-next-btn"
                onClick={() => { if (!form.name.trim()) { setError("Organization name is required"); return; } setError(""); setStep(2); }}
                className="w-full mt-8 bg-slate-900 text-white font-outfit font-semibold py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="animate-slide-up">
              <div className="flex items-center gap-3 mb-2">
                <Gauge className="w-5 h-5 text-slate-700" />
                <h2 className="font-outfit text-2xl font-black text-slate-900">Context Configuration</h2>
              </div>
              <p className="font-ibm-plex text-sm text-slate-500 mb-8">
                SYNQ calibrates question difficulty, scoring sensitivity, and insight depth based on this.
              </p>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    <Factory className="w-3 h-3 inline mr-1" /> Industry
                  </label>
                  <select data-testid="industry-select" value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    <Users className="w-3 h-3 inline mr-1" /> Company Size
                  </label>
                  <select data-testid="company-size-select" value={form.company_size}
                    onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                    {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                      <Gauge className="w-3 h-3 inline mr-1" /> Complexity
                    </label>
                    <select data-testid="complexity-select" value={form.project_complexity}
                      onChange={(e) => setForm({ ...form, project_complexity: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {COMPLEXITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                      <GitBranch className="w-3 h-3 inline mr-1" /> Methodology
                    </label>
                    <select data-testid="methodology-select" value={form.methodology}
                      onChange={(e) => setForm({ ...form, methodology: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-3 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white">
                      {METHODOLOGIES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 font-ibm-plex">{error}</div>
              )}
              <div className="flex gap-3 mt-8">
                <button onClick={() => setStep(1)}
                  className="flex-1 border border-gray-300 text-slate-700 font-outfit font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button data-testid="setup-complete-btn" onClick={handleCreate} disabled={loading}
                  className="flex-1 bg-slate-900 text-white font-outfit font-semibold py-3 rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading ? "Setting up..." : "Complete Setup"} {!loading && <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
